import Form from 'react-bootstrap/Form';
import { Row, Col } from 'react-bootstrap';
import { useEffect, useState, useCallback } from 'react';
import { BiUser, BiBuildingHouse, BiDotsHorizontalRounded, BiCheck, BiErrorCircle } from 'react-icons/bi';
import './style.css';
import {
    getIdentificationTypes,
    getOrganizations,
    getLiabilities,
    getRegimes,
    getMunicipalities
} from '../../../usecases/thirds-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import {
    validateEmail,
    validatePhone,
    validateDocumentNumber,
    validateAddress,
    validatePostalCode
} from '../../../utils/validate';
import { calculateDV } from '../../../utils/fiscal';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import MunicipalitySearchDropdown from '../../molecules/MunicipalitySearchDropdown/view';

function ThirdForm({
    initialData = {},
    onSubmit,
    loadingId,
    title,
    submitLabel,
    onCancel
}) {
    const [catalogs, setCatalogs] = useState({
        identifications: [],
        organizations: [],
        liabilities: [],
        regimes: [],
        municipalities: []
    });

    const [third, setThird] = useState({
        identificationsId: 1,
        typeOrganizationId: 2,
        typeRegimeId: 2,
        typeLiabilityId: 117,
        countryId: 46,
        merchantRegistration: "00000000",
        client: true,
        provider: false,
        other: false,
        ...initialData
    });

    const [errors, setErrors] = useState({});
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);

    useEffect(() => {
        setThird(prev => ({ ...prev, ...initialData }));
        setErrors({});
        loadCatalogs();
    }, [initialData]);

    const loadCatalogs = useCallback(async () => {
        if (loadingCatalogs) return;
        setLoadingCatalogs(true);
        try {
            const [types, orgs, libs, regs, municipalities] = await Promise.all([
                getIdentificationTypes(),
                getOrganizations(),
                getLiabilities(),
                getRegimes(),
                getMunicipalities("")
            ]);
            setCatalogs(prev => ({
                ...prev,
                identifications: types || [],
                organizations: orgs || [],
                liabilities: libs || [],
                regimes: regs || [],
                municipalities: municipalities || []
            }));
        } catch (error) {
            console.error("Error loading catalogs", error);
        } finally {
            setLoadingCatalogs(false);
        }
    }, [loadingCatalogs]);

    const toggleRole = (role) => {
        setThird(prev => {
            const nextRoles = {
                ...prev,
                [role]: !prev[role]
            };
            // Limpiar error de roles si al menos uno queda activo
            if (nextRoles.client || nextRoles.provider || nextRoles.other) {
                setErrors(prevErr => {
                    const auxErr = { ...prevErr };
                    delete auxErr.roles;
                    return auxErr;
                });
            }
            return nextRoles;
        });
    };

    function validateSingleField(field, value, currentThird = third) {
        let fieldError = null;

        switch (field) {
            case 'name':
                if (!value || !value.trim()) {
                    fieldError = 'El nombre o razón social es obligatorio';
                } else if (value.trim().length < 3) {
                    fieldError = 'El nombre debe tener al menos 3 caracteres';
                }
                break;

            case 'identification': {
                const docVal = validateDocumentNumber(value, currentThird.identificationsId);
                if (!docVal.valid) {
                    fieldError = docVal.message;
                }
                break;
            }

            case 'email':
                if (!value || !value.trim()) {
                    fieldError = 'El correo electrónico es obligatorio';
                } else if (!validateEmail(value)) {
                    fieldError = 'Formato de correo electrónico inválido';
                }
                break;

            case 'phone':
                if (!value || !value.trim()) {
                    fieldError = 'El teléfono es obligatorio';
                } else if (!validatePhone(value)) {
                    fieldError = 'Teléfono inválido (debe tener entre 7 y 15 dígitos)';
                }
                break;

            case 'address':
                if (currentThird.provider) {
                    if (!value || !value.trim()) {
                        fieldError = 'La dirección es obligatoria para proveedores';
                    } else if (!validateAddress(value)) {
                        fieldError = 'La dirección debe tener al menos 5 caracteres';
                    }
                }
                break;

            case 'municipalityId':
                if (currentThird.provider && !value) {
                    fieldError = 'El municipio es obligatorio para proveedores';
                }
                break;

            case 'postalZoneCode':
                if (currentThird.provider) {
                    if (!value || !value.trim()) {
                        fieldError = 'El código postal es obligatorio para proveedores';
                    } else if (!validatePostalCode(value)) {
                        fieldError = 'El código postal debe tener al menos 4 dígitos numéricos';
                    }
                }
                break;

            default:
                break;
        }

        setErrors(prev => {
            const next = { ...prev };
            if (fieldError) {
                next[field] = fieldError;
            } else {
                delete next[field];
            }
            return next;
        });

        return fieldError;
    }

    function handleChange(e) {
        const { id, value, type, checked } = e.target;
        const key = id.replace("third-", "");
        let aux = { ...third };

        if (type === 'checkbox') {
            aux[key] = checked;
        } else {
            aux[key] = value;

            if (key === "identification" || key === "identificationsId") {
                const doc = key === "identification" ? value : aux.identification;
                const typeId = parseInt(key === "identificationsId" ? value : aux.identificationsId, 10);

                // ID 6 es standard NIT, ID 2 es fallback
                if (typeId === 6 || typeId === 2) {
                    aux.dv = calculateDV(doc);
                } else {
                    aux.dv = "";
                }

                validateSingleField('identification', doc, { ...aux, identificationsId: typeId });
            } else if (key === "municipalityName") {
                aux.municipalityName = value;
                if (!value) {
                    aux.municipalityId = null;
                    validateSingleField('municipalityId', null, aux);
                }
            } else {
                validateSingleField(key, value, aux);
            }
        }
        setThird(aux);
    }

    function handleBlur(e) {
        const { id, value } = e.target;
        const key = id.replace("third-", "");
        validateSingleField(key, value, third);
    }

    function validateAll() {
        const newErrors = {};

        // 1. Validar al menos un rol
        if (!third.client && !third.provider && !third.other) {
            newErrors.roles = 'Debes seleccionar al menos un tipo de tercero (Cliente, Proveedor u Otro)';
        }

        // 2. Nombre
        if (!third.name || !third.name.trim()) {
            newErrors.name = 'El nombre o razón social es obligatorio';
        } else if (third.name.trim().length < 3) {
            newErrors.name = 'El nombre debe tener al menos 3 caracteres';
        }

        // 3. Documento
        const docValidation = validateDocumentNumber(third.identification, third.identificationsId);
        if (!docValidation.valid) {
            newErrors.identification = docValidation.message;
        }

        // 4. Email
        if (!third.email || !third.email.trim()) {
            newErrors.email = 'El correo electrónico es obligatorio';
        } else if (!validateEmail(third.email)) {
            newErrors.email = 'Formato de correo electrónico inválido';
        }

        // 5. Teléfono
        if (!third.phone || !third.phone.trim()) {
            newErrors.phone = 'El teléfono es obligatorio';
        } else if (!validatePhone(third.phone)) {
            newErrors.phone = 'Teléfono inválido (debe tener entre 7 y 15 dígitos)';
        }

        // 6. Campos fiscales si es proveedor
        if (third.provider) {
            if (!third.address || !third.address.trim()) {
                newErrors.address = 'La dirección es obligatoria para proveedores';
            } else if (!validateAddress(third.address)) {
                newErrors.address = 'La dirección debe tener al menos 5 caracteres';
            }

            if (!third.municipalityId) {
                newErrors.municipalityId = 'El municipio es obligatorio para proveedores';
            }

            if (!third.postalZoneCode || !third.postalZoneCode.trim()) {
                newErrors.postalZoneCode = 'El código postal es obligatorio para proveedores';
            } else if (!validatePostalCode(third.postalZoneCode)) {
                newErrors.postalZoneCode = 'El código postal debe tener al menos 4 dígitos numéricos';
            }
        }

        setErrors(newErrors);
        return newErrors;
    }

    async function handleInternalSubmit() {
        const validationErrors = validateAll();
        const errorKeys = Object.keys(validationErrors);

        if (errorKeys.length > 0) {
            dispatchCustomEvent(`loading-btn-${loadingId}`, {});
            const firstErrorMessage = validationErrors[errorKeys[0]];
            dispatchCustomEvent("popup", {
                message: firstErrorMessage || "Por favor corrige los campos indicados en rojo",
                type: "error"
            });
            return;
        }

        // Sanitización previa al submit
        const sanitizedThird = {
            ...third,
            name: third.name?.trim(),
            identification: String(third.identification)?.trim(),
            email: third.email?.trim()?.toLowerCase(),
            phone: String(third.phone)?.replace(/[\s-]/g, ''),
            address: third.address ? third.address.trim() : '',
            postalZoneCode: third.postalZoneCode ? String(third.postalZoneCode).trim() : ''
        };

        onSubmit(sanitizedThird);
    }

    return (
        <div className="third-modal-content">
            <h2 className="third-form-title">{title}</h2>

            {/* Role Selection Cards */}
            <div className="role-cards-container">
                <div className={`role-card ${third.client ? 'active' : ''}`} onClick={() => toggleRole('client')}>
                    <div className="role-card-info">
                        <BiUser className="role-card-icon" />
                        <span className="role-card-label">Cliente</span>
                    </div>
                    <div className="role-card-check">
                        <BiCheck />
                    </div>
                </div>

                <div className={`role-card ${third.provider ? 'active' : ''}`} onClick={() => toggleRole('provider')}>
                    <div className="role-card-info">
                        <BiBuildingHouse className="role-card-icon" />
                        <span className="role-card-label">Proveedor</span>
                    </div>
                    <div className="role-card-check">
                        <BiCheck />
                    </div>
                </div>

                <div className={`role-card ${third.other ? 'active' : ''}`} onClick={() => toggleRole('other')}>
                    <div className="role-card-info">
                        <BiDotsHorizontalRounded className="role-card-icon" />
                        <span className="role-card-label">Otro</span>
                    </div>
                    <div className="role-card-check">
                        <BiCheck />
                    </div>
                </div>
            </div>

            {errors.roles && (
                <div className="roles-invalid-banner">
                    <BiErrorCircle size={18} />
                    <span>{errors.roles}</span>
                </div>
            )}

            <Form action="javascript:void(0);" id="third-form" onChange={handleChange} style={{ display: "block" }}>
                {/* Section 1: Información Básica */}
                <div className="form-group-cu">
                    {/* Left Side: Title & Description */}
                    <Col md={4} className="form-section-sidebar">
                        <h3 className="section-title-modern">Información Básica</h3>
                        <p className="section-desc-modern">Completa los datos de identidad y contacto primario del tercero.</p>
                    </Col>

                    {/* Right Side: Inputs Grid */}
                    <Col md={16} className='form-section-right'>
                        <Form.Group className="form-group-custom" controlId="third-name">
                            <Form.Label>Nombre / Razón Social <span className="req">*</span></Form.Label>
                            <Form.Control
                                className={`form-control-custom ${errors.name ? 'is-invalid-custom' : ''}`}
                                type="text"
                                placeholder="Ingresa el nombre o razón social"
                                value={third.name || ''}
                                onBlur={handleBlur}
                            />
                            {errors.name && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.name}</span>}
                        </Form.Group>

                        <Row>
                            <Form.Group style={{ marginTop: "8px" }} className="form-group-custom" controlId="third-identificationsId">
                                <Form.Label>Tipo de Documento</Form.Label>
                                <Form.Select
                                    style={{ marginTop: "4px" }}
                                    id="third-identificationsId"
                                    className="form-select-custom"
                                    value={third.identificationsId || ''}
                                >
                                    {Object.keys(catalogs.identifications).length > 0 ?
                                        Object.keys(catalogs.identifications).map(key => (
                                            <option key={key} value={key}>{catalogs.identifications[key]}</option>
                                        )) :
                                        <>
                                            <option value="1">Cédula de ciudadanía</option>
                                            <option value="2">NIT</option>
                                        </>
                                    }
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="form-group-custom" controlId="third-identification">
                                <Form.Label>Número de Documento <span className="req">*</span></Form.Label>
                                <Form.Control
                                    className={`form-control-custom ${errors.identification ? 'is-invalid-custom' : ''}`}
                                    type="text"
                                    placeholder="Ingresa el documento"
                                    value={third.identification || ''}
                                    onBlur={handleBlur}
                                />
                                {errors.identification && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.identification}</span>}
                            </Form.Group>

                            <Form.Group className="form-group-custom" controlId="third-dv">
                                <Form.Label>DV</Form.Label>
                                <Form.Control
                                    className="form-control-custom text-center fw-bold"
                                    type="text"
                                    value={third.dv || ''}
                                    readOnly
                                    disabled
                                    placeholder="-"
                                />
                            </Form.Group>
                        </Row>

                        <Row>
                            <Form.Group className="form-group-custom" controlId="third-email">
                                <Form.Label>Email <span className="req">*</span></Form.Label>
                                <Form.Control
                                    className={`form-control-custom ${errors.email ? 'is-invalid-custom' : ''}`}
                                    type="email"
                                    placeholder="ejemplo@correo.com"
                                    value={third.email || ''}
                                    onBlur={handleBlur}
                                />
                                {errors.email && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.email}</span>}
                            </Form.Group>

                            <Form.Group className="form-group-custom" controlId="third-phone">
                                <Form.Label>Teléfono / Celular <span className="req">*</span></Form.Label>
                                <Form.Control
                                    className={`form-control-custom ${errors.phone ? 'is-invalid-custom' : ''}`}
                                    type="tel"
                                    placeholder="Ej. 3001234567"
                                    value={third.phone || ''}
                                    onBlur={handleBlur}
                                />
                                {errors.phone && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.phone}</span>}
                            </Form.Group>
                        </Row>
                    </Col>
                </div>

                <hr style={{ padding: "0 16px" }}></hr>

                {/* Section 2: Información de Ubicación */}
                <div className="form-group-cu">
                    {/* Left Side: Title & Description */}
                    <Col md={4} className="form-section-sidebar">
                        <h3 className="section-title-modern">Información de Ubicación</h3>
                        <p className="section-desc-modern">Detalles de domicilio y localización física del contacto.</p>
                    </Col>

                    {/* Right Side: Inputs Grid */}
                    <Col md={16} className='form-section-right'>
                        <Row>
                            <Form.Group className="form-group-custom" controlId="third-address">
                                <Form.Label>Dirección {third.provider && <span className="req">*</span>}</Form.Label>
                                <Form.Control
                                    className={`form-control-custom ${errors.address ? 'is-invalid-custom' : ''}`}
                                    type="text"
                                    placeholder="Ej. Calle 123 # 45 - 67"
                                    value={third.address || ''}
                                    required={third.provider}
                                    onBlur={handleBlur}
                                />
                                {errors.address && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.address}</span>}
                            </Form.Group>

                            <Form.Group className="form-group-custom" controlId="third-municipalityId">
                                <Form.Label>Municipio / Ciudad {third.provider && <span className="req">*</span>}</Form.Label>
                                <MunicipalitySearchDropdown
                                    selected={third.municipalityId ? { id: third.municipalityId, name: third.municipalityName || '' } : null}
                                    onSelected={(municipio) => {
                                        if (municipio) {
                                            setThird(prev => ({
                                                ...prev,
                                                municipalityId: municipio.id,
                                                municipalityName: municipio.name,
                                                postalZoneCode: municipio.code || prev.postalZoneCode || ''
                                            }));
                                            validateSingleField('municipalityId', municipio.id);
                                            if (municipio.code) {
                                                validateSingleField('postalZoneCode', municipio.code);
                                            }
                                        } else {
                                            setThird(prev => ({
                                                ...prev,
                                                municipalityId: '',
                                                municipalityName: '',
                                                postalZoneCode: ''
                                            }));
                                            validateSingleField('municipalityId', null);
                                        }
                                    }}
                                />
                                {errors.municipalityId && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.municipalityId}</span>}
                            </Form.Group>
                        </Row>
                    </Col>
                </div>

                {/* Section 3: Información Fiscal (Proveedores) */}
                {third.provider && (
                    <>
                        <hr style={{ padding: "0 16px" }}></hr>
                        <div className="form-group-cu">
                            {/* Left Side: Title & Description */}
                            <Col md={4} className="form-section-sidebar">
                                <h3 className="section-title-modern">Datos Fiscales</h3>
                                <p className="section-desc-modern">Información tributaria y de registro exigida por ley.</p>
                            </Col>

                            {/* Right Side: Inputs Grid */}
                            <Col md={16} className='form-section-right'>
                                <Row>
                                    <Form.Group className="form-group-custom" controlId="third-typeOrganizationId">
                                        <Form.Label>Tipo de Organización</Form.Label>
                                        <Form.Select id="third-typeOrganizationId" className="form-select-custom" value={third.typeOrganizationId || ''}>
                                            {catalogs.organizations.map(item => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="form-group-custom" controlId="third-typeRegimeId">
                                        <Form.Label>Régimen Fiscal</Form.Label>
                                        <Form.Select id="third-typeRegimeId" className="form-select-custom" value={third.typeRegimeId || ''}>
                                            {catalogs.regimes.map(item => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Row>

                                <Row className="mt-2">
                                    <Form.Group className="form-group-custom" controlId="third-typeLiabilityId">
                                        <Form.Label>Responsabilidad Fiscal</Form.Label>
                                        <Form.Select id="third-typeLiabilityId" className="form-select-custom" value={third.typeLiabilityId || ''}>
                                            {catalogs.liabilities.map(item => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="form-group-custom" controlId="third-postalZoneCode">
                                        <Form.Label>Cód. Postal <span className="req">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            className={`form-control-custom text-center fw-bold ${errors.postalZoneCode ? 'is-invalid-custom' : ''}`}
                                            value={third.postalZoneCode || ''}
                                            readOnly
                                            disabled
                                        />
                                        {errors.postalZoneCode && <span className="invalid-feedback-custom"><BiErrorCircle size={14} /> {errors.postalZoneCode}</span>}
                                    </Form.Group>


                                    <Form.Group className="form-group-custom" controlId="third-merchantRegistration">
                                        <Form.Label>Matrícula Mercantil</Form.Label>
                                        <Form.Control
                                            className="form-control-custom"
                                            type="text"
                                            placeholder="00000000"
                                            value={third.merchantRegistration || '00000000'}
                                        />
                                    </Form.Group>
                                </Row>
                            </Col>
                        </div>
                    </>
                )}
            </Form>

            <ButtonPrimary
                onClick={handleInternalSubmit}
                id={loadingId}
                title={submitLabel}
                type="submit"
                showLoading={true}
                className="variant-green"
            />
        </div>
    );
}

export default ThirdForm;

